<template>
  <div
    v-if="planDetails"
    :class="$style.planNameDescription"
    data-test-id="SuggestedPlan__planRecommendation"
  >
    <div :class="$style.suggestedPlanInfo">
      <div v-if="answers.usersCount > 10">
        <HeadingSmall>
          {{ planDetails.displayName }}
        </HeadingSmall>
        <ParagraphBody
          bodySize="small"
          weight="light"
        >
          {{ billingCadenceText }}
        </ParagraphBody>
      </div>
      <div v-if="answers.usersCount > 10">
        <HeadingSmall>
          {{ packageCost }}/user
        </HeadingSmall>
      </div>
      <div v-else>
        <HeadingSmall>
          Free
        </HeadingSmall>
      </div>
    </div>
    <hr>
    <HeadingExtraSmall>
      Estimated Total
    </HeadingExtraSmall>
    <div :class="$style.suggestedPlanInfo">
      <div>
        <ParagraphBody
          bodySize="small"
          weight="light"
        >
          Monthly Price
        </ParagraphBody>
      </div>
      <div>
        <div>
          <HeadingExtraSmall
            weight="bold"
          >
            {{ monthlyTotalCostDisplayText }}
          </HeadingExtraSmall>
        </div>
      </div>
    </div>
    <div
      v-if="isAnnual"
      :class="$style.suggestedPlanInfo"
      data-test-id="SuggestedPlan__annualTotal"
    >
      <div>
        <ParagraphBody
          bodySize="small"
          weight="light"
        >
          Annual Total
        </ParagraphBody>
      </div>
      <div>
        <div>
          <HeadingExtraSmall
            weight="bold"
          >
            {{ annualRateDisplayText }}
          </HeadingExtraSmall>
        </div>
      </div>
    </div>
    <Button
      v-if="answers.usersCount > 10"
      text="buy now"
      textCase="lowercase"
      @click="handleBuyNowClick"
    />
  </div>
  <div
    v-else
    data-test-id="SuggestedPlan__noRecommendation"
  >
    <HeadingExtraSmall>
      Oops! We don't have any recommendations based on your selections
    </HeadingExtraSmall>
    <Button
      icon="calendar"
      text="schedule a call"
      @click="handleScheduleCallClicked"
    />
  </div>
</template>

<script>
import {
  Button,
  HeadingExtraSmall,
  HeadingSmall,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import CheckoutUtil from 'client/GrowthEngineering/utilities/CheckoutUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import PaymentHelper from 'client/Payments/PaymentHelper';
import PaymentStepMixin from 'client/Payments/mixins/PaymentStepMixin';
import Util from 'client/util/Util';

const pricingCalculatorModule = 'PricingCalculatorModule';

const {
  description,
  location,
  text,
  section,
} = eventProperties;
const {
  pricingCalculatorModal,
} = eventLocations;

export default {
  name: 'SuggestedPlan',

  components: {
    Button,
    HeadingExtraSmall,
    HeadingSmall,
    ParagraphBody,
  },

  mixins: [PaymentStepMixin],

  EventHelper,
  Util,

  props: {
    answers: {
      type: Object,
      required: true,
    },
    isAnnual: Boolean,
  },

  computed: {
    ...mapGetters(pricingCalculatorModule, {
      annualRate: 'getAnnualRate',
      annualRateDisplayText: 'getAnnualRateDisplayText',
      monthlyRate: 'getMonthlyRate',
      monthlyTotalCostDisplayText: 'getMonthlyTotalCostDisplayText',
      planDetails: 'getPlanDetails',
    }),

    billingCadenceText() {
      const freeAddendum = this.monthlyRate <= 0 ? ' (free up to 10 users)' : '';
      return this.isAnnual
        ? `*${this.answers.usersCount} users`
        : `*${this.answers.usersCount} users billed monthly${freeAddendum}`;
    },

    packageCost() {
      let packageCost;
      if (this.isAnnual) {
        packageCost = CheckoutUtil.formatDisplayPrice(this.planDetails.annualPrice);
      } else {
        packageCost = CheckoutUtil.formatDisplayPrice(this.planDetails.listPrice);
      }
      return packageCost;
    },
  },

  methods: {
    ...mapActions(pricingCalculatorModule, [
      'setAnswers',
    ]),

    async handleBuyNowClick() {
      const currentBillingOptionsState = this.isAnnual ? 'annual' : 'monthly';
      this.setAnswers(this.answers);
      await this.updateItem(this.planDetails);
      this.$options.EventHelper.triggerButtonClicked({
        [description]: `buy now clicked with billing option set to: ${currentBillingOptionsState}`,
        [text]: 'buy now',
        [location]: pricingCalculatorModal,
        [section]: `Suggested Plan: ${this.planDetails.displayName}`,
      });
      PaymentHelper.openCheckoutModal(pricingCalculatorModal);
    },

    handleScheduleCallClicked() {
      this.$options.Util.openUrl(this.calendlyLink);
    },
  },
};
</script>

<style module>
.planNameDescription {
  background-color: var(--jcPrimary50);
  border-radius: 1rem;
  font-size: 1.8rem;
  line-height: 3.2rem;
  padding: 1rem;
}

.suggestedPlanInfo {
  display: flex;
  justify-content: space-between;
}
</style>
